import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { Filter } from '@pkg/classes';
import {
    type AccountsFilter,
    AccountsFilterProperties,
    type Chain,
    type Collectible,
} from '@pkg/graphql/codegen/graphql';

import { useIsPreview } from '@app/pages/ProfilePage/hooks';
import { useNFTsFiltersStore } from '@app/store/zustand';

export function useSetFilterOptions({
    collectionIds,
    walletAddresses,
}: {
    collectionIds?: string[];
    walletAddresses?: string[];
}) {
    const { availableFilters, setAvailableFilters } = useNFTsFiltersStore(useShallow((s) => s));
    const hasSetOptions = useRef(false);
    useEffect(() => {
        if (hasSetOptions.current) return;

        const filtersClone = new Filter(availableFilters);
        if (walletAddresses?.length) {
            filtersClone.set('wallets', walletAddresses);
        }

        if (collectionIds?.length) {
            filtersClone.set('collections', collectionIds);
        }
        if (collectionIds?.length || walletAddresses?.length) {
            setAvailableFilters(filtersClone);
            hasSetOptions.current = true;
        }
    }, [availableFilters, collectionIds, setAvailableFilters, walletAddresses]);
}
export function useGetFilteredAssets() {
    const { currentFilters } = useNFTsFiltersStore();
    const isPreview = useIsPreview();

    const getFilteredByHidden = useCallback(
        (collectibles?: Collectible[]) => {
            const items = collectibles?.reduce(
                (acc, curr) => {
                    if (curr?.hidden) {
                        acc.hidden = [...acc.hidden, curr];
                    } else {
                        acc.visible = [...acc.visible, curr];
                    }
                    return acc;
                },
                { hidden: [] as Collectible[], visible: [] as Collectible[] },
            ) || { hidden: [] as Collectible[], visible: [] as Collectible[] };

            const showHiddenNfts =
                currentFilters.hasSomeValue('status', 'hidden') &&
                !currentFilters.hasSomeValue('status', 'visibleToEveryone');
            const showVisible =
                currentFilters.hasSomeValue('status', 'visibleToEveryone') &&
                !currentFilters.hasSomeValue('status', 'hidden');

            if (isPreview) return items.visible;
            return showHiddenNfts || showVisible ? (showHiddenNfts ? items.hidden : items.visible) : collectibles;
        },
        [currentFilters, isPreview],
    );

    const chains = useMemo(() => {
        const chainFilterValues = currentFilters?.get('chain');
        return chainFilterValues
            ? chainFilterValues?.map((chain) => chain.toString().toUpperCase() as Chain).filter((c) => !!c)
            : [];
    }, [currentFilters]);

    const statusProperties = useMemo(() => {
        const statusFilterValues = currentFilters.get('status');
        return (
            statusFilterValues
                ? statusFilterValues
                      .map((status) => {
                          switch (status) {
                              case 'listed': {
                                  return AccountsFilterProperties.Asked;
                              }
                              case 'hasOffers': {
                                  return AccountsFilterProperties.Bidded;
                              }
                              case 'spam': {
                                  return AccountsFilterProperties.Spam;
                              }
                              default:
                                  return undefined;
                          }
                      })
                      .filter((s) => !!s)
                : []
        ) as AccountsFilterProperties[];
    }, [currentFilters]);

    const accountsFilter = useMemo(() => {
        const walletsFilterValues = currentFilters.get('wallets');
        const collectionsFilterValues = currentFilters.get('collections');
        const accountsFilter: AccountsFilter = {
            chains: chains?.length ? chains : null,
            properties: statusProperties?.length ? statusProperties : null,
            accounts: walletsFilterValues?.length ? walletsFilterValues : null,
            collectionsIds: collectionsFilterValues?.length ? collectionsFilterValues : null,
        };
        return accountsFilter;
    }, [chains, currentFilters, statusProperties]);

    return { accountsFilter, getFilteredByHidden };
}
